import React from "react";
import {
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from "@mui/material";
import { IApprovalListResponseDto } from "src/services/thumbz-base-api";
import { MoreVertOutlined } from "@mui/icons-material";
import { ApprovalViewProps } from "./approval-view-selector";
import PopupState, { bindTrigger } from "material-ui-popup-state";
import { RenderAvatar } from "src/components/avatar/RenderAvatar";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useThumbzApi } from "src/services/thumbz-api";
import { useConfirm } from "material-ui-confirm";
import { queryKeys } from "src/config/query-key";

export const ApprovalTable: React.FC<ApprovalViewProps> = ({
  onDelete,
  approvals,
  onOpen,
  config,
  refetchApprovals,
}) => {
  const { show_apv_name, show_workspace, show_stages } = config;
  const { api } = useThumbzApi();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const openDialog = ({ apv_id, wrk_id }: { apv_id: string; wrk_id: string }) => {
    onOpen({ apv_id, wrk_id });
  };

  const archiveApprovalMutation = useMutation({
    mutationFn: (variables: { apv_id: string; wrk_id: string }) =>
      api.approval.approvalControllerArchiveApproval({ apv_id: variables.apv_id }),
    onSuccess: (_, variables) => {
      const { wrk_id } = variables;
      toast.success("Aprovação arquivada com sucesso");
      refetchApprovals();
      queryClient.invalidateQueries(queryKeys.approval.list({ wrk_id, archived: false }));
      queryClient.invalidateQueries(queryKeys.approval.list({ wrk_id, archived: true }));
    },
    onError: () => {
      toast.error("Erro ao arquivar aprovação. Tente novamente.");
    },
  });

  const handleArchive = (apv_id: string, wrk_id: string) => {
    confirm({
      title: "Arquivar?",
      description: `Tem certeza que deseja arquivar a aprovação?`,
      confirmationText: "Sim, arquivar",
      cancellationText: "Cancelar",
      confirmationButtonProps: { color: "error" },
    }).then(() => {
      archiveApprovalMutation.mutate({ apv_id, wrk_id });
    });
  };

  return (
    <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
      <Table>
        <TableHead>
          <TableRow>
            {show_apv_name && <TableCell scope="col">Nome</TableCell>}
            <TableCell scope="col">Status</TableCell>
            {show_workspace && <TableCell scope="col">Workspace</TableCell>}
            {show_stages && <TableCell scope="col">Fluxo padrão</TableCell>}
            <TableCell scope="col" align="right">
              Ações
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {approvals.map((approval) => (
            <TableRow
              hover
              key={approval.apv_id}
              onClick={() => openDialog({ apv_id: approval.apv_id, wrk_id: approval.wrk_id })}
              sx={{ cursor: "pointer" }}
            >
              {show_apv_name && <TableCell>{approval.apv_name}</TableCell>}
              <TableCell>{approval.apv_status}</TableCell>
              {show_workspace && (
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <RenderAvatar
                      fallbackSize={25}
                      sx={{ width: 25, height: 25 }}
                      src={approval.wrk_photo_url}
                    />
                    <Typography variant="body2">{approval.wrk_name}</Typography>
                  </Stack>
                </TableCell>
              )}
              {show_stages && <TableCell>{approval.flow_name}</TableCell>}
              <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                <PopupState variant="popover" popupId={`approval-menu-${approval.apv_id}`}>
                  {(popupState) => (
                    <>
                      <IconButton {...bindTrigger(popupState)}>
                        <MoreVertOutlined />
                      </IconButton>
                      <Menu
                        anchorEl={popupState.anchorEl}
                        open={Boolean(popupState.anchorEl)}
                        onClose={() => popupState.close()}
                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                        transformOrigin={{ vertical: "top", horizontal: "center" }}
                      >
                        <MenuItem
                          onClick={() => {
                            popupState.close();
                            openDialog({ apv_id: approval.apv_id, wrk_id: approval.wrk_id });
                          }}
                        >
                          <ListItemText>Visualizar</ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            popupState.close();
                            handleArchive(approval.apv_id, approval.wrk_id);
                          }}
                        >
                          <ListItemText>Arquivar</ListItemText>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            popupState.close();
                            onDelete({ apv_id: approval.apv_id, wrk_id: approval.wrk_id });
                          }}
                        >
                          <ListItemText>Excluir</ListItemText>
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </PopupState>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
